import { inject, Injectable } from '@angular/core';
import { BaseValidationService, ValidationResult, kd } from '@luzula/luzula-lib';
import { AuthService } from '../auth.service';
import { DataService } from '../data.service';
import { ProjectService } from '../project.service';
import { AufnahmePunktDocument, punktartFieldWarning } from '../data-model';
import { isValidWasserform } from './wasserform-validator';
import { isVegetationDuplicate } from './vegetation-duplicate';
import { istBestandOk } from './bestand/bestand-parser';

@Injectable({
	providedIn: 'root'
})
export class ValidationService extends BaseValidationService {
	private auth = inject(AuthService);
	private ds = inject(DataService);
	private ps = inject(ProjectService);

	constructor() {
		super();
		// Bohrpunkte-spezifische Validatoren registrieren

		// beispielhaft für Sandbox-Liste
		this.registerValidator("aListOption", (data) => {
			const num = data as string | number;
			switch (num) {
				case '4':
				case 4: return this.warning("Ganz schön viel");
				case '5':
				case 5: return this.danger("Bitte nicht die 5!!");
				default: return this.ok();
			}
		});

		this.registerValidator("Vegetations-Anteil", (s: unknown) => {
			// Leere Werte sind ok - erst validieren wenn etwas eingetragen wurde
			if (s === undefined || s === null || s === '') return this.ok();
			const str = String(s).trim();
			if (str === '') return this.ok();
			if (str === 'r') return this.ok();
			if (str === '+') return this.ok();
			const n = parseFloat(str);
			if (n <= 0 || n > 100 || isNaN(n)) return this.danger("Ungültiger Wert (Zahl oder r,+)");
			else return this.ok();
		});

		const wasserformValidator = (s: unknown) => {
			const str = s as string;
			if (!str) return this.ok();
			if (!isValidWasserform(str)) return this.danger("Nur Ziffern, + und x erlaubt");
			return this.ok();
		};
		this.registerValidator("Wasserform", wasserformValidator);
		this.registerValidator("Wasserform2", wasserformValidator);

		// Punktart: eine nicht (mehr) in der aktuellen Liste vorhandene Punktart
		// (Alt-Punkt mit entfernter/umbenannter Nummer) ist nur eine WARNUNG, kein
		// blockierender Fehler — der Punkt bleibt so editierbar/anpassbar. Bekannte
		// Werte (Label / Beschreibung / reine Nummer) sind ok; leer faellt unter den
		// nonEmpty-Pflichtfeld-Check. Greift fuer Voll- (PunktartMitProtokoll) und
		// Kurzprotokoll (Punktart) gleichermassen.
		const punktartValidator = (s: unknown) => {
			const msg = punktartFieldWarning(s as string);
			return msg ? this.warning(msg) : this.ok();
		};
		this.registerValidator("Punktart", punktartValidator);
		this.registerValidator("PunktartMitProtokoll", punktartValidator);

		// Bestand: nicht SEA-konformer Text wird gelb markiert (Konverter macht ihn
		// grün). Eine bewusst abweichende Beschreibung kann der Nutzer per Knopf
		// bestätigen — dann steht der bestätigte Text in `extendedHeader.bestandBestaetigt`
		// und die Validierung wertet den Bestand als „ok".
		this.registerValidator("Bestand", (s: unknown, ctx?: unknown) => {
			const bestaetigt = (ctx as AufnahmePunktDocument | undefined)?.extendedHeader?.bestandBestaetigt;
			return istBestandOk(s as string, bestaetigt) ? this.ok() : this.warning('Bestand nicht SEA-konform');
		});
	}

	/**
	 * Erweitert die Basis-Validierung um die felduebergreifende Dubletten-Pruefung
	 * der Vegetationsarten. Der Basis-Validierungspfad sieht pro Feld nur den
	 * Einzelwert; die Dublette ist aber nur im Kontext der ganzen Vegetationsliste
	 * (inkl. Schicht der jeweiligen Zeile) erkennbar. Der volle Feldname
	 * "Vegetation[i]" liefert hier den Zeilenindex.
	 */
	override validate(key: string, data: unknown): ValidationResult {
		const base = super.validate(key, data);
		const parsed = kd(key);
		if (parsed.key !== 'Vegetation' || parsed.index === undefined) return base;
		const veg = (this.context as AufnahmePunktDocument | undefined)
			?.bodenvegetation?.aufnahme?.vegetationen;
		if (!isVegetationDuplicate(veg, parsed.index)) return base;
		const dup = this.warning('Art in dieser Schicht bereits erfasst');
		return dup.type > base.type ? dup : base;
	}

	/**
	 * Gibt den aktuellen Benutzernamen zurück
	 */
	protected override getCurrentUser(): string {
		return this.auth.currentUser;
	}

	/**
	 * Speichert den aktuellen Kontext (Bohrpunkt) mit den Kommentaren
	 */
	protected override saveContext(): void {
		if (!this.context) return;
		// Defense-in-depth: Diesen (Kommentar-/Validierungs-)Save nur ausführen, wenn
		// der User den Punkt tatsächlich mutieren darf. `canIWritePoint` ist das
		// korrekte, status-/projekt-/frozen-aware Tor:
		//  - Kontrolleur-Stift (in_pruefung) → canIWritePoint=true (canEditPoint),
		//  - Bearbeiter erledigt Kommentare auf eigenem bearbeitung-Punkt → true,
		//  - Vorgelegt (kontrolle)/abgeschlossen/fremdes Projekt → false → kein Save.
		//
		// FRÜHERE LÜCKE (2026-06-24): Hier stand zusätzlich `|| canActAsKontrolleur()`,
		// um Kommentieren auf `kontrolle`-Punkten zu erlauben. Das war status-/projekt-
		// agnostisch und ließ einen Kontrolleur Fehler-Kommentare auf `bearbeitung`-/
		// `abgeschlossen`-/fremdprojektige Punkte schreiben (Stift gehört nur in
		// `in_pruefung`, s. PERMISSIONS.md §5.2 + WorkflowService.canKontrolleurReview).
		if (!this.ps.canIWritePoint(this.context as any)) return;
		this.setValidationState(); // ValidationState aktualisieren vor dem Speichern
		this.ds.saveDocument(this.context, { user: this.auth.currentUser });
	}
}
